import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faIndustry, faChartLine, faClock } from "@fortawesome/free-solid-svg-icons";

const ProductionSuggestions = ({ suggestions = [], loading = false }) => {
  const [hoveredSuggestionId, setHoveredSuggestionId] = useState(null);
  const [hoveredNameId, setHoveredNameId] = useState(null);
  if (loading) {
    return (
      <div className="bg-white p-3 rounded-lg shadow">
        <div className="flex items-center space-x-2 justify-center py-4">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-xs text-neutral-600">Carregando...</span>
        </div>
      </div>
    );
  }

  // Garantir que suggestions é um array
  const suggestionsArray = Array.isArray(suggestions) ? suggestions : [];

  if (!suggestionsArray.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow">
        <h3 className="text-xs font-semibold mb-2 flex items-center">
          <FontAwesomeIcon icon={faIndustry} className="mr-2 text-blue-600" size="sm" />
          Sugestões de Produção
        </h3>
        <div className="text-center py-2">
          <p className="text-xs text-neutral-500">Sem sugestões</p>
          <p className="text-xs text-neutral-400">Estoque adequado</p>
        </div>
      </div>
    );
  }

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'URGENT': return { icon: faExclamationTriangle, color: 'text-red-600', borderColor: 'border-l-red-600' };
      case 'HIGH': return { icon: faExclamationTriangle, color: 'text-yellow-600', borderColor: 'border-l-yellow-600' };
      case 'MEDIUM': return { icon: faChartLine, color: 'text-blue-600', borderColor: 'border-l-blue-600' };
      case 'LOW': return { icon: faIndustry, color: 'text-green-600', borderColor: 'border-l-green-600' };
      default: return { icon: faIndustry, color: 'text-blue-500', borderColor: 'border-l-blue-500' };
    }
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'URGENT': 'Urgente',
      'HIGH': 'Alta',
      'MEDIUM': 'Média',
      'LOW': 'Baixa'
    };
    return labels[priority] || priority;
  };

  const getPriorityBadgeColor = (priority) => {
    switch(priority) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-yellow-100 text-yellow-800';
      case 'MEDIUM': return 'bg-blue-100 text-blue-800';
      case 'LOW': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const renderScoreTooltip = (suggestion) => {
    const score = suggestion.priority_score || 0;
    const breakdown = suggestion.priority_breakdown || {};

    const getPriorityColor = (priority) => {
      switch(priority) {
        case 'URGENT': return 'bg-red-500';
        case 'HIGH': return 'bg-yellow-500';
        case 'MEDIUM': return 'bg-blue-500';
        case 'LOW': return 'bg-green-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="absolute bottom-full left-0 mb-2 bg-neutral-800 text-white text-xs rounded p-2 whitespace-nowrap z-10 shadow-lg">
        <div className="font-semibold mb-1">
          Prioridade: <span className={`${getPriorityColor(suggestion.production_priority)} px-2 py-0.5 rounded text-white`}>{score} pts</span>
        </div>
        <div className="text-xs space-y-0.5">
          <div>📦 Estoque: {breakdown.stock_ratio_points || 0} pts</div>
          <div>📊 Demanda: {breakdown.demand_points || 0} pts</div>
          <div>⏱️ Cura: {breakdown.curing_points || 0} pts</div>
          <div>⏰ Dias: {breakdown.days_until_stockout_points || 0} pts</div>
          <div>📈 Variabilidade: {breakdown.variability_points || 0} pts</div>
        </div>
      </div>
    );
  };

  const renderFormulaTooltip = (suggestion) => {
    if (!suggestion.formula) return null;
    const breakdown = suggestion.formula.breakdown || {};
    const entries = Object.entries(breakdown);

    return (
      <div className="absolute top-full left-0 mt-1 bg-neutral-800 text-white text-xs rounded p-2 z-20 shadow-lg" style={{ minWidth: '200px', maxWidth: '280px' }}>
        <div className="font-semibold mb-1 text-neutral-100">{suggestion.formula.name}</div>
        <div className="font-mono text-neutral-400 mb-1 text-xs break-all">{suggestion.formula.expression}</div>
        {entries.length > 0 && (
          <div className="border-t border-neutral-600 pt-1 mt-1 space-y-0.5">
            {entries.map(([key, val]) => (
              <div key={key} className="flex justify-between gap-2">
                <span className="text-neutral-400 truncate">{key}</span>
                <span className="text-green-400 flex-shrink-0">
                  {val !== null ? `${Number(val).toFixed(2)} kg` : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-neutral-600 pt-1 mt-1 flex justify-between">
          <span className="text-neutral-400">sugestão</span>
          <span className="text-blue-400 font-bold">
            {(suggestion.suggested_quantity ?? suggestion.suggestedQuantity ?? 0).toFixed(2)} kg
          </span>
        </div>
      </div>
    );
  };

  // Mostrar todas as sugestões com quantidade > 0 (exclui apenas OK sem necessidade)
  const displayedSuggestions = suggestionsArray;

  return (
    <div className="bg-white p-3 rounded-lg shadow flex flex-col max-h-[320px]">
      <h3 className="text-xs font-semibold flex items-center mb-2 flex-shrink-0">
        <FontAwesomeIcon icon={faIndustry} className="mr-2 text-blue-600" size="sm" />
        Sugestões de Produção
      </h3>

      {/* Lista compacta - apenas sugestões altas */}
      <div className="space-y-1 overflow-y-auto flex-1 min-h-0">
        {displayedSuggestions.length > 0 ? (
          displayedSuggestions.map((suggestion) => {
            const priorityConfig = getPriorityIcon(suggestion.production_priority);
            const badgeColor = getPriorityBadgeColor(suggestion.production_priority);
            const isCured = suggestion.is_cured && suggestion.curing_time_days > 0;

            return (
              <div
                key={suggestion.id}
                className={`p-1.5 bg-neutral-50 rounded border-l-2 ${priorityConfig.borderColor} hover:bg-neutral-100 transition-colors relative`}
                onMouseEnter={() => setHoveredSuggestionId(suggestion.id)}
                onMouseLeave={() => setHoveredSuggestionId(null)}
              >
                {/* Tooltip aparece ao passar mouse */}
                {hoveredSuggestionId === suggestion.id && renderScoreTooltip(suggestion)}

                <div className="flex items-center justify-between gap-1">
                  <div className="flex-1 min-w-0 relative">
                    <p
                      className="font-medium text-xs text-neutral-900 truncate cursor-help"
                      onMouseEnter={() => setHoveredNameId(suggestion.id)}
                      onMouseLeave={() => setHoveredNameId(null)}
                    >
                      {suggestion.name}
                    </p>
                    {hoveredNameId === suggestion.id && renderFormulaTooltip(suggestion)}
                    {isCured && (
                      <p className="text-xs text-orange-600">
                        Cura: {suggestion.curing_time_days}d
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-blue-600 text-xs">
                      {(suggestion.suggested_quantity ?? suggestion.suggestedQuantity ?? 0).toFixed(0)} kg
                    </p>
                    {/* Badge de prioridade */}
                    <p className={`text-xs px-1 py-0 rounded ${badgeColor} inline-block whitespace-nowrap`}>
                      {getPriorityLabel(suggestion.production_priority)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-2">
            <p className="text-xs text-neutral-500">Sem sugestões altas</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default ProductionSuggestions;
